import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Link from 'next/link';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Button } from 'planoplan-ui-kit/core/button';
import { useTranslations } from '@libs/translations';
import { useEditorIframe } from '@libs';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { TimeRanges } from '@components/Help-Center-Components/time-ranges';
import { ArticleCard } from '@components/Help-Center-Components/article-card';
import { VideoCard } from '@components/Help-Center-Components';
import { Points, textSize, marginLevel, common } from '@styles';
import { HELP_CENTER, OVERAGE_READING_TIME } from '@constants';

const TABLET_WIDTH = '600px';
const LAPTOP_WIDTH = '640px';
const FULLHD_WIDTH = '690px';

export const LearningModulePage = ({ data }) => {
  const { locale } = useSelector((state) => state.settings);
  const translations = useTranslations();
  const isOpenInEditorIframe = useEditorIframe();

  const links = [
    {
      href: `/${HELP_CENTER.MAIN}`,
      name: translations.t('site.help.title'),
    },
    {
      href: `/${locale}/${HELP_CENTER.MAIN}/${HELP_CENTER.LEARNING_MODULES}`,
      name: `${data.sectionCaption}`,
    },
    {
      href: `/${locale}/${HELP_CENTER.MAIN}/${HELP_CENTER.LEARNING_MODULES}/${data.slug}`,
      name: `${data.caption}`,
    },
  ];

  const downloadTriggerWord = {
    ru: 'Скачать',
    en: 'Download',
    de: 'Herunterladen',
    es: 'Descargar',
  };

  const [allowHover, setAllowHover] = useState(true);

  useEffect(() => {
    const onEnter = () => {
      if (allowHover === false) {
        setAllowHover(true);
      }
    };

    window.addEventListener('mouseover', onEnter, { passive: true });

    return () => {
      window.removeEventListener('mouseover', onEnter);
    };
  }, [allowHover]);

  return (
    <Wrapper>
      <TopWrap>
        <Breadcrumbs links={links} isOpenInEditorIframe={isOpenInEditorIframe} />
        <Title>{data.caption}</Title>
        <InfoWrap>
          <TimeRanges time={data.time ?? OVERAGE_READING_TIME} locale={locale} />
          <Views>{data.views}</Views>
        </InfoWrap>
      </TopWrap>
      <ContentWrap>
        <Description dangerouslySetInnerHTML={{ __html: data.body }} />
        {data.articles.length > 0 && (
          <ArticlesBlock>
            <SectionTitle>{translations.t('site.help.articlesTitle')}</SectionTitle>
            <ArticleContentWrap isOpenInEditorIframe={isOpenInEditorIframe}>
              {data.articles
                ?.map((item) => ({ ...item, slug: `${item.slug}/?course_id=${data.id}` }))
                .map((item) => (
                  <ArticleCard
                    locale={locale}
                    key={item.id}
                    sectionUrl={item.sectionSlug}
                    category={item.categorySlug}
                    slug={item.slug}
                    {...item}
                  />
                ))}
            </ArticleContentWrap>
          </ArticlesBlock>
        )}
        {data.videos.length > 0 && (
          <VideosBlock>
            <SectionTitle>{translations.t('site.help.videosTitle')}</SectionTitle>
            <VideoContentWrap isOpenInEditorIframe={isOpenInEditorIframe}>
              {data.videos
                ?.map((item) => ({ ...item, slug: `${item.slug}/?course_id=${data.id}` }))
                .map((item) => (
                  <VideoCard
                    locale={locale}
                    key={item.id}
                    sectionUrl={item.sectionSlug}
                    category={item.categorySlug}
                    slug={item.slug}
                    {...item}
                  />
                ))}
            </VideoContentWrap>
          </VideosBlock>
        )}
        {data.hotkeys && data.hotkeys.length > 0 && (
          <HotkeySection>
            <SectionTitle>{translations.t('site.help.hotkeysTitle')}</SectionTitle>
            {data.hotkeys
              ?.map((item) => ({ ...item, slug: item.slug.length > 0 && `${item.slug}/?course_id=${data.id}` }))
              .map((item, index) => (
                <KeysList key={`${index}-${item.id}`}>
                  {item.slug ? (
                    <KeyRow>
                      <Link
                        href={`/${HELP_CENTER.MAIN}/${item.sectionSlug}/${item.categorySlug}/${item.slug}`}
                        passHref>
                        <KeyRowLink>
                          <KeyCaption>{item.description}</KeyCaption>
                          <KeyDescription>{item.caption}</KeyDescription>
                        </KeyRowLink>
                      </Link>
                    </KeyRow>
                  ) : (
                    <KeyRow key={item.slug}>
                      <KeyCaption>{item.description}</KeyCaption>
                      <KeyDescription>{item.caption}</KeyDescription>
                    </KeyRow>
                  )}
                </KeysList>
              ))}
          </HotkeySection>
        )}
        {data.resources && data.resources.length > 0 && (
          <ResourcesSection>
            <SectionTitle>{translations.t('site.help.additionalMaterialTitle')}</SectionTitle>
            <CardsWrap>
              {data.resources.map((item) => (
                <Card key={item.id} allowHover={allowHover}>
                  <CardTitle>{item.caption}</CardTitle>
                  <CardDescription dangerouslySetInnerHTML={{ __html: item.description }} />
                  <Button
                    theme="neutral_secondary"
                    size="S"
                    href={item.buttonUrl}
                    blank
                    rel="noopener noreferrer"
                    widthText
                    onClick={() => {
                      setAllowHover(false);
                    }}
                    style={{ width: 'fit-content', marginTop: 'auto' }}>
                    {item.buttonTitle.includes(downloadTriggerWord[locale]) && (
                      <SvgWrap>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M1.66797 9.33398V13.0007C1.66797 13.737 2.26492 14.334 3.0013 14.334H13.0013C13.7377 14.334 14.3346 13.737 14.3346 13.0007V9.33398"
                            stroke="#292929"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 1.33333L8 10M8 10L12 6M8 10L4 6"
                            stroke="#292929"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </SvgWrap>
                    )}
                    {item.buttonTitle}
                  </Button>
                </Card>
              ))}
            </CardsWrap>
          </ResourcesSection>
        )}
      </ContentWrap>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin: 55px 0 0;
  width: 100%;
  overflow: auto;

  @media ${Points.tablet_landscape} {
    margin: 30px 28px 0;
    width: calc(100% - 46px);
  }

  @media ${Points.laptop_wide} {
    margin: 40px auto;
    width: 1280px;
  }

  @media ${Points.fullhd} {
    margin: 40px auto;
    width: 1386px;
  }
`;

const TopWrap = styled.div`
  margin: 0 16px;

  @media ${Points.tablet_landscape} {
    margin: 0;
  }
`;

const Title = styled.h1`
  margin: 35px 0 15px;
  ${textSize('page_header')};
  font-weight: 200;
  color: ${Colors.main_black};
  width: 100%;

  @media ${Points.tablet_landscape} {
    margin: 35px 0 20px;
    width: ${TABLET_WIDTH};
  }

  @media ${Points.laptop_wide} {
    margin: 40px 0 25px;
    width: ${LAPTOP_WIDTH};
  }

  @media ${Points.fullhd} {
    margin: 50px 0 20px;
    width: ${FULLHD_WIDTH};
  }
`;

const InfoWrap = styled.div`
  display: flex;
  gap: 20px;
`;

const Views = styled.div`
  padding-left: 21px;
  background-image: url('/images/help/views.png');
  background-position: left center;
  background-repeat: no-repeat;
  background-size: 16px;
  ${textSize('description')};
  color: ${Colors.second_gray};
  margin-top: 5px;

  @media ${Points.fullhd} {
    margin-top: 10px;
  }
`;

const ContentWrap = styled.div``;

const Description = styled.div`
  margin: 40px 16px 80px;

  & > *:last-child {
    margin-bottom: 0;
  }

  @media ${Points.tablet_landscape} {
    width: ${TABLET_WIDTH};
    margin: 70px 0 100px;
  }

  @media ${Points.laptop_wide} {
    margin: 80px 0 120px;
    width: ${LAPTOP_WIDTH};
  }

  @media ${Points.fullhd} {
    margin: 90px 0 160px;
    width: ${FULLHD_WIDTH};
  }

  h2 {
    color: ${Colors.main_dark};
    ${textSize('large_card_header')};
    ${marginLevel('bottom', 'accent_outer')};
    ${marginLevel('top', 'blog_header')};
  }

  .interview-question {
    ${textSize('h3_interview')};
    font-weight: 400;
    color: ${Colors.main_dark};
    ${marginLevel('bottom', 'interview')};
    ${marginLevel('top', 'accent_outer')};
  }

  .video {
    position: relative;
    display: flex;
    padding-top: 56.25%;

    iframe {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
  }

  figure {
    ${marginLevel('top', 'blog_img')};
    ${marginLevel('bottom', 'accent_outer')};
    margin-left: auto;
    margin-right: auto;
  }

  figure:not(.video):not(.column):not(.mount) {
    @media ${Points.laptop} {
      width: ${LAPTOP_WIDTH};
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_WIDTH};
    }
  }

  figcaption {
    margin-top: 5px;
    font-size: 12px;
    line-height: 1.115;
    color: ${Colors.main_gray};
    text-align: right;

    @media ${Points.fullhd} {
      font-size: 14px;
    }
  }

  img {
    width: 100%;
    display: block;
  }

  .column,
  .column img {
    width: 100%;
  }

  .border img {
    border: 1px solid #e5e5e5;
  }

  .image-pair-wrapper {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  .mount {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 24px;
    background-color: #656565;

    @media ${Points.tablet_portrait} {
      padding-top: 25px;
    }

    @media ${Points.laptop} {
      padding-top: 30px;
    }
  }

  .mount img {
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 28px;

    @media ${Points.tablet_portrait} {
      width: fit-content;
      margin-bottom: 30px;
    }

    @media ${Points.laptop} {
      margin-bottom: 35px;
    }
  }

  .mount figcaption {
    background-color: #ffffff;
    width: 100%;
    padding-top: 5px;
  }

  .visual-heading {
    margin-bottom: 10px;
    font-weight: 300;
    font-size: 22px;
    line-height: 1.2;

    @media ${Points.tablet_portrait} {
      font-size: 24px;
      margin-left: auto;
      margin-right: auto;
    }

    @media ${Points.laptop} {
      line-height: 1.1;
    }

    @media ${Points.fullhd} {
      font-size: 30px;
      line-height: 1.115;
    }
  }

  p:not(.bluebox p):not(.ctabox p):not(.list__content p):not(.visual-heading):not(.quote-text p) {
    color: ${Colors.main_gray};
    margin-bottom: 20px;
    ${textSize('article')};

    @media ${Points.tablet_portrait} {
      margin-left: auto;
      margin-right: auto;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    ${common('listBlog')};
  }

  ol,
  ul {
    color: ${Colors.main_gray};
    ${marginLevel('bottom', 'accent_outer')};
    padding-left: 20px;

    @media ${Points.tablet_portrait} {
      margin-left: auto;
      margin-right: auto;
    }
  }

  .list__content ul {
    margin-bottom: 0;
  }

  li {
    ${textSize('article')};
    margin-bottom: 10px;

    @media ${Points.laptop} {
      margin-bottom: 14px;
    }
  }

  a:not(.bluebox-button):not(.cta-button):not(.cta-button-text) {
    ${common('a')};
    color: ${Colors.second_link};
  }

  .list {
    display: flex;
    justify-content: start;
    ${marginLevel('both', 'whisper')};

    p {
      padding: 0;
    }
  }

  .list + :not(.list) {
    ${marginLevel('top', 'accent_outer')};
  }

  b,
  strong:not(.list__content strong):not(.bluebox strong):not(.ctabox strong) {
    font-weight: 500;
  }

  br {
    height: 24px;

    @media ${Points.tablet_portrait} {
      height: 25px;
    }

    @media ${Points.laptop} {
      height: 20px;
    }

    @media ${Points.fullhd} {
      height: 30px;
    }
  }

  br + br {
    height: 24px;

    @media ${Points.tablet_portrait} {
      height: 20px;
    }

    @media ${Points.laptop} {
      height: 20px;
    }

    @media ${Points.fullhd} {
      height: 20px;
    }
  }

  .bluebox,
  .ctabox,
  .quote {
    width: 100%;
  }

  section {
    margin-bottom: 80px;

    @media ${Points.tablet_landscape} {
      margin-bottom: 100px;
    }

    @media ${Points.laptop_wide} {
      margin-bottom: 120px;
    }

    @media ${Points.fullhd} {
      margin-bottom: 160px;
    }
  }
`;

const ArticlesBlock = styled.section`
  margin-bottom: 80px;

  @media ${Points.tablet_landscape} {
    margin-bottom: 100px;
  }

  @media ${Points.laptop_wide} {
    margin-bottom: 120px;
  }

  @media ${Points.fullhd} {
    margin-bottom: 160px;
  }

  article:last-child {
    margin-right: 16px;

    @media ${Points.tablet_landscape} {
      margin-right: 0;
    }
  }
`;

const VideosBlock = styled.section`
  margin-bottom: 80px;

  @media ${Points.tablet_landscape} {
    margin-bottom: 100px;
  }

  @media ${Points.laptop_wide} {
    margin-bottom: 120px;
  }

  @media ${Points.fullhd} {
    margin-bottom: 160px;
  }

  article:last-child {
    margin-right: 16px;

    @media ${Points.tablet_landscape} {
      margin-right: 0;
    }
  }
`;

const HotkeySection = styled.section`
  margin-bottom: 80px;
  width: 100%;

  @media ${Points.tablet_landscape} {
    margin-bottom: 100px;
    width: ${TABLET_WIDTH};
  }

  @media ${Points.laptop_wide} {
    margin-bottom: 120px;
    width: ${LAPTOP_WIDTH};
  }

  @media ${Points.fullhd} {
    margin-bottom: 160px;
    width: ${FULLHD_WIDTH};
  }
`;

const ResourcesSection = styled.section`
  margin-bottom: 80px;
  width: 100%;

  @media ${Points.tablet_landscape} {
    margin-bottom: 100px;
  }

  @media ${Points.laptop_wide} {
    margin-bottom: 80px;
  }

  @media ${Points.fullhd} {
    margin-bottom: 120px;
  }
`;

const ArticleContentWrap = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: ${(p) => (p.isOpenInEditorIframe ? 'wrap' : 'nowrap')};
  overflow-x: auto;
  gap: 15px;
  width: calc(100% - 16px);
  margin-left: 16px;

  @media ${Points.tablet_landscape} {
    display: grid;
    grid-template-columns: 312px 312px 312px;
    gap: 15px;
    margin-left: 0;
    width: 100%;
  }

  @media ${Points.laptop_wide} {
    grid-template-columns: 305px 305px 305px 305px;
    gap: 20px;
  }

  @media ${Points.fullhd} {
    grid-template-columns: 330px 330px 330px 330px;
  }
`;

const VideoContentWrap = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: ${(p) => (p.isOpenInEditorIframe ? 'wrap' : 'nowrap')};
  overflow-x: auto;
  gap: 15px;
  width: calc(100% - 16px);
  margin-left: 16px;

  @media ${Points.tablet_landscape} {
    display: grid;
    grid-template-columns: 440px 440px;
    gap: 15px;
    margin-left: 0;
    width: 100%;
  }

  @media ${Points.laptop_wide} {
    grid-template-columns: 413px 413px 413px;
    gap: 20px;
  }

  @media ${Points.fullhd} {
    grid-template-columns: 448px 448px 448px;
  }
`;

const SectionTitle = styled.h2`
  ${textSize('block_header')};
  font-weight: 200;
  margin: 0 16px 32px;

  @media ${Points.tablet_landscape} {
    margin: 0;
    margin-bottom: 35px;
  }

  @media ${Points.laptop_wide} {
    margin-bottom: 40px;
  }
`;

const KeysList = styled.ul`
  ${Mixins.resetList()};
  display: flex;
  flex-direction: column;
  margin: 0 16px;

  @media ${Points.tablet_landscape} {
    margin: 0;
  }
`;

const KeyRow = styled.li`
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid ${Colors.dark_200};

  @media ${Points.fullhd} {
    padding: 20px 0;
    gap: 20px;
  }

  &:has(a)::after {
    position: absolute;
    content: '';
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='13' height='10' viewBox='0 0 13 10' fill='none'%3e%3cpath d='M0.666016 5L11.9994 5M11.9994 5L8.2183 1M11.9994 5L8.2183 9' stroke='%23292929' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    width: 16px;
    height: 16px;
    right: 0;
    top: 20px;

    @media ${Points.tablet_landscape} {
      display: none;
    }
  }

  &:has(a):active {
    border-color: ${Colors.blue_500};
    color: ${Colors.main_dark};
  }

  @media (hover: hover) and (pointer: fine) {
    &:has(a):hover {
      border-color: ${Colors.blue_500};
      color: ${Colors.main_dark};
    }

    &:has(a):hover::after {
      display: block;
      position: absolute;
      content: '';
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='13' height='10' viewBox='0 0 13 10' fill='none'%3e%3cpath d='M0.666016 5L11.9994 5M11.9994 5L8.2183 1M11.9994 5L8.2183 9' stroke='%23292929' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      width: 16px;
      height: 16px;
      right: 0;
      top: 20px;

      @media ${Points.fullhd} {
        top: 27px;
      }
    }
  }
`;

const KeyRowLink = styled.a`
  ${Mixins.resetLink()};
  display: flex;
  flex-wrap: nowrap;
  gap: 15px;
  width: inherit;
  position: relative;
  width: 100%;
  color: ${Colors.main_grey};
`;

const KeyCaption = styled.div`
  width: 125px;
  min-width: 125px;
  text-wrap-mode: wrap;
  white-space: break-spaces;
  word-break: break-word;

  @media ${Points.tablet_landscape} {
    width: 220px;
    min-width: 220px;
  }

  @media ${Points.fullhd} {
    width: 240px;
    min-width: 240px;
  }
`;

const KeyDescription = styled.div`
  padding-right: 31px;
`;

const CardsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 0 16px;

  @media ${Points.tablet_landscape} {
    display: grid;
    grid-template-columns: 440px 440px;
    gap: 15px;
    margin: 0;
  }

  @media ${Points.laptop_wide} {
    grid-template-columns: 413px 413px;
    gap: 20px;
  }

  @media ${Points.fullhd} {
    grid-template-columns: 448px 448px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.dark_200};
  padding: 25px;
  width: 343px;

  @media ${Points.tablet_landscape} {
    width: 100%;
    height: 100%;
    padding: 30px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: ${(p) => p.allowHover && '#c3d6ff'};
    }
    a:hover {
      background-color: ${(p) => (p.allowHover ? `` : `${Colors.white} !important`)};
    }
  }
`;

const CardTitle = styled.h2`
  ${textSize('card_header')};
  font-weight: 300;
  margin-bottom: 20px;

  @media ${Points.tablet_landscape} {
    margin-bottom: 5px;
  }
`;

const CardDescription = styled.div`
  margin-bottom: 30px;

  @media ${Points.tablet_landscape} {
    margin-bottom: 20px;
  }

  ul {
    ${textSize('body')}

    @media ${Points.tablet_landscape} {
      padding-left: 34px;
    }
  }

  li {
    position: relative;
    list-style: none;
    margin-bottom: 10px;

    &::before {
      content: '';
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3e%3cpath d='M3.5 11.5L9 17L20.5 5.5' stroke='%232B35EE' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
      width: 24px;
      height: 24px;
      position: absolute;
      left: -32px;
    }

    @media ${Points.tablet_landscape} {
      margin-bottom: 16px;
    }
  }
`;

const SvgWrap = styled.span`
  margin-right: 8px;
`;
